import { useScienceTutorSection } from './scienceTutorContext';
import { CheckCircle2, Flame, RotateCcw, XCircle } from 'lucide-react';
import { useState } from 'react';

import {
  EMITTERS,
  FLAME_COLOUR_LABELS,
  FLAME_COLOUR_OPTIONS,
  FLAME_EMITTERS,
  FLAME_UNKNOWNS,
  VISIBLE_MAX_NM,
  VISIBLE_MIN_NM,
  checkFlamePrediction,
  checkSpectrumIdentification,
  photonEnergyEv,
  unknownSpectrumLines,
  wavelengthToRgb,
  type Emitter,
  type EmitterId,
  type FlameColourId,
} from './instrumentalAnalysis';

const SPECTRUM_HEIGHT = 44;
const SPECTRUM_LABEL_HEIGHT = 14;
const SPECTRUM_LINE_WIDTH = 1.6;
const SPECTRUM_TICKS_NM = [400, 500, 600, 700] as const;
const SPECTRUM_WIDTH = VISIBLE_MAX_NM - VISIBLE_MIN_NM;

export function Spectrum({ lines, label }: { lines: number[]; label: string }) {
  return (
    <figure className="min-w-0">
      <svg
        viewBox={`0 0 ${SPECTRUM_WIDTH} ${SPECTRUM_HEIGHT + SPECTRUM_LABEL_HEIGHT}`}
        className="block h-auto w-full"
        role="img"
        aria-label={`${label}: lines at ${lines.map((line) => line.toFixed(1)).join(', ')} nm`}
      >
        <rect x={0} y={0} width={SPECTRUM_WIDTH} height={SPECTRUM_HEIGHT} rx={4} className="fill-ink" />
        {lines.map((line) => (
          <rect
            key={line}
            x={line - VISIBLE_MIN_NM - SPECTRUM_LINE_WIDTH / 2}
            y={3}
            width={SPECTRUM_LINE_WIDTH}
            height={SPECTRUM_HEIGHT - 6}
            style={{ fill: wavelengthToRgb(line) }}
          />
        ))}
        {SPECTRUM_TICKS_NM.map((tick) => (
          <text
            key={tick}
            x={tick - VISIBLE_MIN_NM}
            y={SPECTRUM_HEIGHT + SPECTRUM_LABEL_HEIGHT - 3}
            textAnchor="middle"
            className="fill-slate2 text-[9px] font-bold"
          >
            {tick} nm
          </text>
        ))}
      </svg>
      <figcaption className="mt-1 text-xs font-bold text-ink-soft">
        {label}: {lines.map((line) => line.toFixed(1)).join(', ')} nm
      </figcaption>
    </figure>
  );
}

function FlameSwatch({ emitter }: { emitter: Emitter }) {
  const colours = emitter.swatchWavelengthsNm.map(wavelengthToRgb);
  const background =
    colours.length === 1
      ? `radial-gradient(ellipse at 50% 70%, ${colours[0]} 0%, ${colours[0]} 35%, transparent 75%)`
      : `radial-gradient(ellipse at 50% 70%, ${colours.join(', ')}, transparent 80%)`;
  const label = emitter.flameColour ? FLAME_COLOUR_LABELS[emitter.flameColour] : 'No flame test';
  return (
    <div className="flex items-center gap-4 rounded-2xl border border-hairline bg-ink p-4">
      <div
        className="h-24 w-16 shrink-0 rounded-t-full rounded-b-[40%] opacity-90"
        style={{ background }}
        role="img"
        aria-label={`Model flame colour for ${emitter.symbol}: ${label}`}
      />
      <div>
        <p className="text-[0.65rem] font-extrabold uppercase tracking-[0.14em] text-stone2">
          Flame colour (model)
        </p>
        <p className="mt-1 text-lg font-extrabold text-white">{label}</p>
        <p className="mt-1 text-xs font-medium leading-5 text-stone2">
          Screen colours approximate; use the label and the wavelengths.
        </p>
      </div>
    </div>
  );
}

const LEVEL_TOP = 18;
const LEVEL_BOTTOM = 92;

function EnergyLevels({ emitter }: { emitter: Emitter }) {
  const energy = photonEnergyEv(emitter.mainLineNm);
  const colour = wavelengthToRgb(emitter.mainLineNm);
  return (
    <figure className="rounded-2xl border border-hairline bg-white p-4">
      <svg
        viewBox="0 0 240 110"
        className="block h-auto w-full max-w-sm"
        role="img"
        aria-label={`Energy-level model: an electron absorbs heat, rises to a higher level, then falls back and emits a ${emitter.mainLineNm.toFixed(1)} nm photon of ${energy.toFixed(2)} eV`}
      >
        <line x1={20} x2={150} y1={LEVEL_TOP} y2={LEVEL_TOP} className="stroke-ink" strokeWidth={2} />
        <line x1={20} x2={150} y1={LEVEL_BOTTOM} y2={LEVEL_BOTTOM} className="stroke-ink" strokeWidth={2} />
        <text x={154} y={LEVEL_TOP + 4} className="fill-ink text-[9px] font-bold">excited</text>
        <text x={154} y={LEVEL_BOTTOM + 4} className="fill-ink text-[9px] font-bold">lower level</text>
        <line x1={55} x2={55} y1={LEVEL_BOTTOM - 4} y2={LEVEL_TOP + 6} className="stroke-slate2" strokeWidth={2} strokeDasharray="4 3" />
        <polygon points={`51,${LEVEL_TOP + 10} 59,${LEVEL_TOP + 10} 55,${LEVEL_TOP + 3}`} className="fill-slate2" />
        <text x={30} y={58} className="fill-slate2 text-[8px] font-bold">heat</text>
        <line x1={110} x2={110} y1={LEVEL_TOP + 4} y2={LEVEL_BOTTOM - 6} style={{ stroke: colour }} strokeWidth={3} />
        <polygon points={`105,${LEVEL_BOTTOM - 10} 115,${LEVEL_BOTTOM - 10} 110,${LEVEL_BOTTOM - 2}`} style={{ fill: colour }} />
        <text x={116} y={50} className="fill-ink text-[8px] font-bold">
          {emitter.mainLineNm.toFixed(1)} nm
        </text>
        <text x={116} y={62} className="fill-ink text-[8px] font-bold">
          ΔE = {energy.toFixed(2)} eV
        </text>
      </svg>
      <figcaption className="mt-2 text-sm font-medium leading-6 text-ink-soft">
        Heat excites electrons to higher energy levels. When an electron falls back it emits a
        photon with ΔE = hc ÷ λ. Only certain jumps are possible, so each element emits only
        certain wavelengths.
      </figcaption>
    </figure>
  );
}

export function FlameMode() {
  const [emitterId, setEmitterId] = useState<EmitterId>(FLAME_EMITTERS[0].id);
  const [prediction, setPrediction] = useState<FlameColourId | ''>('');
  const [revealed, setRevealed] = useState(false);
  const [unknownId, setUnknownId] = useState(FLAME_UNKNOWNS[0].id);
  const [guess, setGuess] = useState<EmitterId | ''>('');
  const [matchChecked, setMatchChecked] = useState(false);
  useScienceTutorSection('FlameMode', { emitterId, prediction, revealed, unknownId, guess, matchChecked });

  const emitter = FLAME_EMITTERS.find((candidate) => candidate.id === emitterId) ?? FLAME_EMITTERS[0];
  const result = revealed && prediction ? checkFlamePrediction(emitter, prediction) : null;
  const unknown = FLAME_UNKNOWNS.find((candidate) => candidate.id === unknownId) ?? FLAME_UNKNOWNS[0];
  const matchResult = matchChecked && guess ? checkSpectrumIdentification(unknown, guess) : null;

  const chooseEmitter = (id: EmitterId) => {
    setEmitterId(id);
    setPrediction('');
    setRevealed(false);
  };

  const chooseUnknown = (id: string) => {
    setUnknownId(id);
    setGuess('');
    setMatchChecked(false);
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-6 xl:grid-cols-[300px_minmax(0,1fr)]">
        <aside className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
            Choose a metal ion
          </p>
          <div className="mt-4 grid grid-cols-2 gap-2 xl:grid-cols-1">
            {FLAME_EMITTERS.map((candidate) => (
              <button
                key={candidate.id}
                type="button"
                aria-pressed={candidate.id === emitter.id}
                onClick={() => chooseEmitter(candidate.id)}
                className={`rounded-2xl border px-4 py-3 text-left transition-colors ${
                  candidate.id === emitter.id
                    ? 'border-brand-sky bg-wash-sky text-ink'
                    : 'border-hairline bg-white text-slate2 hover:border-brand-sky/40'
                }`}
              >
                <span className="block text-sm font-extrabold">{candidate.symbol}</span>
                <span className="block text-xs font-medium">{candidate.name}</span>
              </button>
            ))}
          </div>
        </aside>

        <article className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8">
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">
            Predict before heating
          </p>
          <h2 className="mt-2 text-2xl font-extrabold text-ink">
            What colour will {emitter.symbol} give in a flame?
          </h2>
          <div
            className="mt-5 grid gap-2 sm:grid-cols-2"
            role="group"
            aria-label="Predict the flame colour"
          >
            {FLAME_COLOUR_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={prediction === option}
                disabled={revealed}
                onClick={() => setPrediction(option)}
                className={`rounded-2xl border px-4 py-3 text-left text-sm font-extrabold transition-colors disabled:cursor-not-allowed ${
                  prediction === option
                    ? 'border-brand-coral bg-wash-coral text-ink'
                    : 'border-hairline bg-white text-ink-soft hover:border-brand-coral/40'
                }`}
              >
                {FLAME_COLOUR_LABELS[option]}
              </button>
            ))}
          </div>
          <div className="mt-5 flex flex-wrap gap-3">
            <button
              type="button"
              disabled={!prediction || revealed}
              onClick={() => setRevealed(true)}
              className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Flame className="h-4 w-4" aria-hidden="true" /> Hold in the flame
            </button>
            <button
              type="button"
              onClick={() => chooseEmitter(emitter.id)}
              className="inline-flex items-center gap-2 rounded-full border border-hairline px-5 py-3 text-sm font-extrabold text-ink-soft hover:bg-surface"
            >
              <RotateCcw className="h-4 w-4" aria-hidden="true" /> Try again
            </button>
          </div>

          {result ? (
            <div className="mt-6 space-y-5">
              <div
                role="status"
                className={`rounded-2xl border p-4 ${
                  result.correct
                    ? 'border-brand-mint/45 bg-wash-mint'
                    : 'border-brand-coral/45 bg-wash-coral'
                }`}
              >
                <p className="flex items-center gap-2 font-extrabold text-ink">
                  {result.correct ? (
                    <CheckCircle2 className="h-5 w-5 text-brand-mint" aria-hidden="true" />
                  ) : (
                    <XCircle className="h-5 w-5 text-brand-coral" aria-hidden="true" />
                  )}
                  {result.correct ? 'Correct prediction' : 'Not this colour'}
                </p>
                <p className="mt-2 text-sm font-medium leading-6 text-ink-soft">{result.feedback}</p>
              </div>
              <div className="grid gap-5 lg:grid-cols-2">
                <div className="space-y-4">
                  <FlameSwatch emitter={emitter} />
                  <Spectrum lines={emitter.linesNm} label={`${emitter.symbol} emission lines`} />
                </div>
                <EnergyLevels emitter={emitter} />
              </div>
            </div>
          ) : null}
        </article>
      </div>

      <section
        aria-labelledby="unknown-spectrum-heading"
        className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8"
      >
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
          Identify an unknown
        </p>
        <h2 id="unknown-spectrum-heading" className="mt-2 text-2xl font-extrabold text-ink">
          Match every line to a reference spectrum
        </h2>
        <label className="mt-4 block text-sm font-extrabold text-ink" htmlFor="flame-unknown">
          Unknown spectrum
        </label>
        <select
          id="flame-unknown"
          value={unknown.id}
          onChange={(event) => chooseUnknown(event.target.value)}
          className="mt-2 w-full max-w-xs rounded-xl border border-hairline bg-white px-3 py-2 text-sm font-bold text-ink"
        >
          {FLAME_UNKNOWNS.map((candidate) => (
            <option key={candidate.id} value={candidate.id}>
              {candidate.label}
            </option>
          ))}
        </select>
        <div className="mt-4 rounded-2xl border border-brand-sky/30 bg-wash-sky p-4">
          <Spectrum lines={unknownSpectrumLines(unknown)} label={unknown.label} />
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-2">
          {EMITTERS.map((reference) => (
            <Spectrum key={reference.id} lines={reference.linesNm} label={`${reference.symbol} reference`} />
          ))}
        </div>

        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-end">
          <div>
            <label className="block text-sm font-extrabold text-ink" htmlFor="flame-guess">
              Your identification
            </label>
            <select
              id="flame-guess"
              value={guess}
              onChange={(event) => {
                setGuess(event.target.value as EmitterId | '');
                setMatchChecked(false);
              }}
              className="mt-2 w-full max-w-xs rounded-xl border border-hairline bg-white px-3 py-2 text-sm font-bold text-ink"
            >
              <option value="">Choose a species</option>
              {EMITTERS.map((reference) => (
                <option key={reference.id} value={reference.id}>
                  {reference.symbol} · {reference.name}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            disabled={!guess}
            onClick={() => setMatchChecked(true)}
            className="rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            Check the match
          </button>
        </div>
        {matchResult ? (
          <div
            role="status"
            aria-label="Spectrum match feedback"
            className={`mt-5 rounded-2xl border p-4 text-sm font-medium leading-6 text-ink-soft ${
              matchResult.correct
                ? 'border-brand-mint/45 bg-wash-mint'
                : 'border-brand-coral/45 bg-wash-coral'
            }`}
          >
            {matchResult.feedback}
          </div>
        ) : null}
      </section>
    </div>
  );
}
